/*
 * File: ClientDrawer.tsx
 * Project: a-a
 * File Created: Thursday, 18th September 2025 5:32:29 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 26th November 2025 11:30:14 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useState } from 'react';
import CustomDrawer from '../../../../../components/shared/CustomDrawer/CustomDrawer';
import { Box, Chip, Divider, Typography } from '@mui/material';
import zeplinColor from '../../../../../theme/Color';
import style from './ClientDrawer.module.css';
import AtheleteForm from '../Forms/AtheleteForm';
import CustomButton from '../../../../../components/shared/CustomButton/CustomButton';
import TalentForm from '../Forms/TalentForm';
import BusinessForm from '../Forms/BusinessForm';
import { ClientType } from '../../../../../utilities/constants';
import CustomModal from '../../../../../components/shared/CustomModal/CustomModal';
import CustomCard from '../../../../../components/shared/CustomCard/CustomCard';
import { ClientService } from '../../../../../service/client.service';

const ClientDrawer = ({
  drawerState,
  typeSelected,
  setDrawerState,
  getAllClient,
  data,
  platformSelected,
  setPlatformSelected,
}: {
  drawerState: boolean;
  typeSelected: string;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  getAllClient: () => Promise<void>;
  data?: any;
  platformSelected?: string;
  setPlatformSelected?: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const [selectedItems, setSelectedItems] = useState<{
    salutation: string[];
    agency: any[];
    league: string[];
    team: string[];
    position: string[];
    number: string[];
    status: string[];
    college: string[];
    time: string[];
    client: boolean[];
    type: string[];
    industry: string[];
    category: string[];
    captiv8: any[];
  }>({
    salutation: [],
    agency: data?.parentId
      ? [{ Name: data?.parent, Id: data?.parentID }]
      : data?.agencyID
      ? [{ Name: data?.agency, Id: data?.agencyID }]
      : [''],
    league: data?.league ? [data?.league] : [],
    team: data?.team ? [data?.team] : [],
    position: data?.position ? [data?.position] : [],
    number: [],
    status: data?.status ? [data?.status] : [],
    college: data?.college ? [data?.college] : [],
    time: [],
    client: [],
    type: data?.type ? [data?.type] : [],
    industry: data?.industry ? [data?.industry] : [],
    category: data?.category ? [data?.category] : [],
    captiv8: data?.captiv8Id ? [{ Id: data?.captiv8Id, Name: '' }] : [],
  });

  const [openModal, setOpenModal] = React.useState(false);
  const handleSelection = (item: any, type: string | boolean) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(open);
    sessionStorage.removeItem('sf_path');
    sessionStorage.removeItem('sf_redirect');
    sessionStorage.removeItem('sf_edit');
    sessionStorage.removeItem('sf_delete');
    setPlatformSelected && setPlatformSelected('Huddle');
  };
  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{
            // position: 'sticky',
            // top: 0,
            zIndex: 1000,
            backgroundColor: zeplinColor.Neutral1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: { xs: '24px 24px 24px 24px', xl: '40px 24px 24px 24px' },
            }}
          >
            {data?.accountId ? (
              <Typography variant='responsiveHeading2'>Edit Client</Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Typography variant='responsiveHeading2'>New Client: {typeSelected}</Typography>
                <Chip
                  label={
                    <Typography
                      variant='responsiveLabel1'
                      sx={{ display: 'flex', alignItems: 'center' }}
                      color={
                        platformSelected === 'Salesforce'
                          ? zeplinColor.Neutral1
                          : zeplinColor.BasicBlack
                      }
                    >
                      {platformSelected}
                    </Typography>
                  }
                  sx={{
                    backgroundColor:
                      platformSelected === 'Salesforce' ? zeplinColor.Blue : zeplinColor.Brand2,
                    color:
                      platformSelected === 'Salesforce'
                        ? zeplinColor.Neutral1
                        : zeplinColor.BasicBlack,
                    '&.MuiChip-root': {
                      height: '24px',
                      padding: '5px 12px',
                      borderRadius: '50px',
                    },
                    '& .MuiChip-label': {
                      padding: 0,
                    },
                  }}
                />
              </Box>
            )}
            <img
              src={'/assets/close-icon.svg'}
              className={style.icon}
              alt='close'
              style={{ cursor: 'pointer' }}
              onClick={() => setOpenModal(true)}
            />
          </Box>
          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
          <Box sx={{ padding: '24px 24px 0px 24px' }}>
            {typeSelected === ClientType.Athlete && (
              <AtheleteForm
                handleSelection={handleSelection}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                getAllClient={getAllClient}
                onClose={setDrawerState}
                data={data}
                platformSelected={platformSelected}
              />
            )}
            {typeSelected === ClientType.Talent && (
              <TalentForm
                handleSelection={handleSelection}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                getAllClient={getAllClient}
                onClose={setDrawerState}
                data={data}
                platformSelected={platformSelected}
              />
            )}
            {typeSelected === ClientType.Business && (
              <BusinessForm
                handleSelection={handleSelection}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                getAllClient={getAllClient}
                onClose={setDrawerState}
                data={data}
                platformSelected={platformSelected}
              />
            )}
          </Box>
          {/* <Box
            sx={{
              width: '100%',
              position: 'sticky',
              bottom: '0px',
              margin: 'auto',
              zIndex: '100',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,
            }}
          >
            <CustomButton
              disabled={disable || formLoading}
              onClick={() => {}}
              variant='contained'
              color={disable ? 'customDisable' : 'primary'}
              style={{
                borderRadius: '14px',
                height: 56,
                width: '100%',
                '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              }}
              textStyle={{ color: disable ? zeplinColor.Neutral8 : zeplinColor.Neutral1 }}
              text={'Create Client'}
              // tabindex={4}
            />
          </Box> */}
        </Box>
      </Box>
      <CustomModal handleClose={handleClose} open={openModal}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,

              // width: '312px',
              // height: '201px',/
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '22px', xl: '32px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                  Discard Changes?
                </Typography>
                <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                  The client details you entered will be lost if you
                  <br /> close this form now.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',

                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Box
                  onClick={handleClose}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 20px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                  >
                    <Typography variant='responsiveButtonMedium' color={zeplinColor.BasicBlack}>
                      Continue Editing
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={onClose(false)}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 20px',
                      backgroundColor: zeplinColor.Error,
                      cursor: 'pointer',
                      // border: `1px solid ${zeplinColor.BasicBlack}`,
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                  >
                    <Typography
                      variant='responsiveButtonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      Yes, Exit
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
          {/* {loading ? <CustomLoader /> : ''} */}
        </Box>
      </CustomModal>
    </CustomDrawer>
  );
};

export default ClientDrawer;
